import {
  AlertTriangle,
  CalendarDays,
  CheckCheck,
  ChevronDown,
  Plus,
  RefreshCw,
  Sparkles,
  Target,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { BearyGuide } from '../ui/BearySprite';
import SegmentedTabs from '../ui/SegmentedTabs';
import {
  EmptyState,
  inputClassName,
  SectionCard,
  SectionTitle,
  StatusPill,
} from '../ui/ScreenPrimitives';

const toneLabels = {
  free: 'Open',
  light: 'Light',
  normal: 'Normal',
  busy: 'Busy',
  overloaded: 'Packed',
};

const urgencyTones = {
  high: 'error',
  medium: 'warning',
  low: 'accent',
  none: 'neutral',
};

function formatDateTime(value, options = {}) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, options).format(date);
}

function formatDateKey(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDateOnlyUtc(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDayLabel(value) {
  const date = parseDateOnlyUtc(value);
  if (!date) return value || '';
  return new Intl.DateTimeFormat(undefined, {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function addDateDays(value, days) {
  if (!value) return null;
  const date = parseDateOnlyUtc(value);
  if (!date) return null;
  date.setUTCDate(date.getUTCDate() + days);
  return formatDateKey(date);
}

function formatTime(value, timezone = null) {
  return formatDateTime(value, {
    hour: 'numeric',
    minute: '2-digit',
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

function formatGeneratedAt(value) {
  return formatDateTime(value, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }) || 'Not generated';
}

function eventIsHappeningNow(item, now = new Date()) {
  if (!item?.startAt) return false;
  const start = new Date(item.startAt);
  const end = item.endAt ? new Date(item.endAt) : new Date(start.getTime() + 60 * 60 * 1000);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return false;
  }

  return start <= now && now < end;
}

function ScopePill({ scope }) {
  return (
    <StatusPill tone={scope === 'family' ? 'family' : 'accent'}>
      {scope === 'family' ? 'Family' : 'Personal'}
    </StatusPill>
  );
}

function CompleteCircle({ item, onCompleteTask, className }) {
  const isCompletable = item?.sourceType === 'task' || item?.sourceType === 'reminder';
  const canComplete = isCompletable && item?.completedAt == null;
  const isCompleted = isCompletable && item?.completedAt != null;
  const isRoutine = item?.itemKind === 'routine';

  if (!canComplete && !isCompleted) {
    return null;
  }

  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition',
        isCompleted
          ? 'border-emerald-600 bg-emerald-600 text-white'
          : 'border-slate-300 bg-white text-transparent hover:border-emerald-500 hover:bg-emerald-50',
        className
      )}
      onClick={(event) => {
        event.stopPropagation();
        if (canComplete) {
          onCompleteTask(item);
        }
      }}
      disabled={isCompleted}
      aria-pressed={isCompleted}
      aria-label={isRoutine ? `Mark ${item?.title || 'routine'} done for today` : `Mark ${item?.title || 'task'} complete`}
    >
      {isCompleted ? <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" /> : null}
    </button>
  );
}

function ClickableArticle({ item, onOpenItem, children, className }) {
  const canOpen = item?.sourceType === 'calendar' || item?.sourceType === 'task';

  if (!canOpen) {
    return <article className={className}>{children}</article>;
  }

  return (
    <article
      className={cn('cursor-pointer', className)}
      role="button"
      tabIndex={0}
      onClick={() => onOpenItem(item)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenItem(item);
        }
      }}
      aria-label={`Open ${item?.title || 'source item'}`}
    >
      {children}
    </article>
  );
}

function getItemMeta(item) {
  if (item?.sourceType === 'calendar') {
    return [item.location, item.description].filter(Boolean).join(' | ');
  }

  return [item.description, item.reason].filter(Boolean).join(' | ');
}

function TimelineRow({ item, section, active, muted, timezone, onOpenItem, onCompleteTask }) {
  const time = item?.sourceType === 'calendar' ? formatTime(item.startAt, timezone) : formatTime(item.dueAt, timezone);
  const meta = getItemMeta(item);
  const Icon = item?.sourceType === 'calendar' ? CalendarDays : item?.completedAt ? CheckCheck : Target;

  return (
    <div className="grid gap-4 sm:grid-cols-[46px_minmax(0,1fr)_auto]">
      <div className="relative flex justify-center">
        <span
          className={cn(
            'relative z-10 inline-flex h-10 w-10 items-center justify-center rounded-full text-white shadow-subtle',
            active ? 'bg-app-accent' : muted ? 'bg-slate-300' : item?.scope === 'family' ? 'bg-emerald-700' : 'bg-slate-700'
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="absolute bottom-[-28px] top-10 w-px bg-app-border/80" />
      </div>

      <ClickableArticle
        item={item}
        onOpenItem={onOpenItem}
        className={cn(
          'rounded-[22px] border p-5 transition',
          active
            ? 'border-app-accent/25 bg-blue-50/70 shadow-panel'
            : muted
              ? 'border-app-border/60 bg-app-surface-alt/55 opacity-80'
              : 'border-transparent bg-transparent hover:border-app-border/70 hover:bg-app-surface/75'
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone={active ? 'accent' : muted ? 'neutral' : 'success'}>{section}</StatusPill>
          <ScopePill scope={item?.scope} />
          {item?.sourceType !== 'calendar' ? (
            <StatusPill tone={urgencyTones[item?.urgency] || 'neutral'}>
              {item?.urgency === 'none' ? 'No urgency' : item?.urgency || 'Normal'}
            </StatusPill>
          ) : null}
        </div>
        <div className="mt-3 flex items-center gap-3">
          <CompleteCircle item={item} onCompleteTask={onCompleteTask} />
          <h3 className="m-0 text-lg font-semibold text-app-ink">{item?.title || 'Untitled item'}</h3>
        </div>
        {meta ? <p className="mt-2 text-sm leading-6 text-app-muted">{meta}</p> : null}
      </ClickableArticle>

      <div className="hidden pt-5 sm:block">
        <span className="rounded-xl bg-blue-50 px-3 py-2 text-sm font-semibold text-app-accent-strong">
          {time || 'Any time'}
        </span>
      </div>
    </div>
  );
}

function RailItem({ item, icon: Icon, tone = 'neutral', onOpenItem, onCompleteTask }) {
  return (
    <ClickableArticle
      item={item}
      onOpenItem={onOpenItem}
      className="rounded-[18px] border border-app-border/60 bg-app-surface-alt/65 p-4 transition hover:border-app-border hover:bg-app-surface"
    >
      <div className="flex gap-3">
        <span className={cn('mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full', tone === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700')}>
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <CompleteCircle item={item} onCompleteTask={onCompleteTask} />
            <h3 className="m-0 text-sm font-semibold text-app-ink">{item?.title || 'Untitled item'}</h3>
          </div>
          <p className="mt-1 text-sm leading-5 text-app-muted">{getItemMeta(item) || item?.body || item?.reason || 'No extra context.'}</p>
        </div>
      </div>
    </ClickableArticle>
  );
}

function MiniMonth({ selectedDate }) {
  const base = selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)
    ? new Date(`${selectedDate}T12:00:00`)
    : new Date();
  const selectedDay = base.getDate();
  const days = Array.from({ length: 14 }, (_, index) => {
    const day = new Date(base);
    day.setDate(selectedDay - 6 + index);
    return day;
  });

  return (
    <SectionCard>
      <SectionTitle
        title="Monthly pulse"
        copy={formatDateTime(base, { month: 'long', year: 'numeric' })}
        action={<StatusPill tone="accent">View</StatusPill>}
      />
      <div className="mt-5 grid grid-cols-7 gap-2 text-center">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, index) => (
          <span key={`${label}-${index}`} className="text-xs font-semibold text-app-muted">
            {label}
          </span>
        ))}
        {days.map((day) => {
          const active = day.toDateString() === base.toDateString();
          return (
            <span
              key={day.toISOString()}
              className={cn(
                'rounded-full py-2 text-sm font-medium',
                active ? 'bg-blue-100 text-app-accent-strong' : 'text-app-ink'
              )}
            >
              {day.getDate()}
            </span>
          );
        })}
      </div>
    </SectionCard>
  );
}

function CollapsedBriefingSection({ title, count, children }) {
  return (
    <details className="group rounded-[22px] border border-app-border/70 bg-app-surface/95 p-5 shadow-subtle lg:p-6">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
        <span className="min-w-0">
          <span className="block text-lg font-semibold text-app-ink">{title}</span>
          <span className="mt-1 block text-sm text-app-muted">
            {count} item{count === 1 ? '' : 's'}
          </span>
        </span>
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-app-surface-alt text-app-muted transition group-open:rotate-180">
          <ChevronDown className="h-5 w-5" aria-hidden="true" />
        </span>
      </summary>
      <div className="mt-5 border-t border-app-border/60 pt-5">{children}</div>
    </details>
  );
}

function CompactGrid({ items, emptyTitle, emptyCopy, renderItem }) {
  if (!items?.length) {
    return <EmptyState title={emptyTitle} copy={emptyCopy} className="py-8 lg:py-8" />;
  }

  return <div className="grid gap-4 xl:grid-cols-2">{items.map(renderItem)}</div>;
}

function RangeAgenda({ briefing, onOpenItem, onCompleteTask }) {
  const days = briefing?.range?.days || [];
  const displayEndDate = addDateDays(briefing?.range?.endDate, -1) || briefing?.range?.endDate;

  return (
    <SectionCard className="p-7 lg:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="m-0 text-xl font-semibold text-app-ink">Schedule</h2>
          <p className="mt-1 text-sm leading-6 text-app-muted">
            {briefing?.range?.startDate ? formatDayLabel(briefing.range.startDate) : 'Range'} through{' '}
            {displayEndDate ? formatDayLabel(displayEndDate) : 'range end'}
          </p>
        </div>
        <StatusPill tone="accent">{briefing?.range?.counts?.scheduled || 0} scheduled</StatusPill>
      </div>

      <div className="mt-7 space-y-7">
        {days.some((day) => day.items?.length) ? (
          days.map((day) => (
            <div key={day.date} className="border-t border-app-border/60 pt-5 first:border-t-0 first:pt-0">
              <h3 className="m-0 text-sm font-semibold uppercase tracking-[0.18em] text-app-muted">
                {formatDayLabel(day.date)}
              </h3>
              <div className="mt-4 space-y-4">
                {day.items?.length ? (
                  day.items.map((item) => (
                    <TimelineRow
                      key={item.itemKey || item.sourceId}
                      item={item}
                      section="Scheduled"
                      timezone={briefing?.timezone}
                      onOpenItem={onOpenItem}
                      onCompleteTask={onCompleteTask}
                    />
                  ))
                ) : (
                  <p className="text-sm text-app-muted">No scheduled events.</p>
                )}
              </div>
            </div>
          ))
        ) : (
          <EmptyState title="No scheduled events." copy="Nothing is on the calendar for this range." className="py-12 lg:py-12" />
        )}
      </div>
    </SectionCard>
  );
}

function DailyBriefingView({
  briefing,
  filters,
  status,
  isLoading,
  onFilterChange,
  onRefresh,
  onOpenItem,
  onCompleteTask,
}) {
  const now = new Date();
  const isRangeBriefing = Boolean(briefing?.range);
  const happeningNow = (briefing?.fixedSchedule || []).filter((item) => eventIsHappeningNow(item, now));
  const comingUp = (briefing?.fixedSchedule || []).filter((item) => !eventIsHappeningNow(item, now));
  const routineItems = briefing?.routinesToday || [];
  const mustDoItems = [...(briefing?.overdue || []), ...(briefing?.mustDoToday || [])];
  const winsCount = briefing?.alreadyDoneToday?.length || 0;
  const prepCount = briefing?.tomorrowPrep?.length || 0;
  const flowItems = [
    ...happeningNow.map((item) => ({ item, section: 'Schedule', active: true })),
    ...comingUp.map((item) => ({ item, section: 'Schedule' })),
    ...routineItems.map((item) => ({ item, section: 'Routines' })),
    ...mustDoItems.map((item) => ({ item, section: 'Tasks' })),
    ...(briefing?.goodSmallWins || []).map((item) => ({ item, section: 'Small win' })),
  ];
  const syncItems = [
    ...(briefing?.bestNextThing ? [briefing.bestNextThing] : []),
    ...mustDoItems.slice(0, briefing?.bestNextThing ? 1 : 2),
  ];
  const headsUpItems = briefing?.headsUp || [];
  const statusTone = status?.type === 'idle' ? 'neutral' : status?.type;

  return (
    <section className="w-full max-w-[1520px] text-app-ink">
      <div className="flex flex-col gap-5 border-b border-app-border/70 pb-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="m-0 text-3xl font-semibold tracking-tight text-app-accent-strong">Calendar Briefing</h1>
            <StatusPill tone={statusTone}>{status?.message || 'Ready'}</StatusPill>
          </div>
          <p className="mt-2 text-sm leading-6 text-app-muted">
            {briefing?.summary || 'A calendar-first view of what is active, what is next, and what can wait.'}
          </p>
          {isLoading ? (
            <BearyGuide
              className="mt-4 max-w-xl"
              preset="loading"
              size={76}
              message="Beary is refreshing the briefing."
            />
          ) : null}
        </div>

        <button
          type="button"
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-app-accent px-5 py-4 text-sm font-semibold text-white shadow-panel transition hover:bg-app-accent-strong disabled:opacity-60"
          onClick={() => onRefresh()}
          disabled={isLoading}
        >
          {isLoading ? <RefreshCw className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Plus className="h-5 w-5" aria-hidden="true" />}
          <span>{isLoading ? 'Refreshing' : 'Refresh briefing'}</span>
        </button>
      </div>

      <div className="mt-8 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <SegmentedTabs
          value={filters.mode}
          onValueChange={(value) => onFilterChange('mode', value)}
          ariaLabel="Briefing mode"
          listClassName="bg-blue-50/80"
          items={[
            { value: 'today', label: 'Today' },
            { value: 'tomorrow', label: 'Tomorrow' },
            { value: 'week', label: 'Week' },
            { value: 'month', label: 'Month' },
          ]}
        />
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedTabs
            value={filters.scope}
            onValueChange={(value) => onFilterChange('scope', value)}
            ariaLabel="Briefing scope"
            items={[
              { value: 'auto', label: 'All' },
              { value: 'personal', label: 'Personal' },
              { value: 'family', label: 'Family' },
            ]}
          />
          <input
            type="date"
            value={filters.date}
            onChange={(event) => onFilterChange('date', event.target.value)}
            className={cn(inputClassName, 'w-auto min-w-[168px]')}
            aria-label="Briefing date"
          />
        </div>
      </div>

      <div className="mt-8 grid gap-7 2xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-7">
          {isRangeBriefing ? (
            <RangeAgenda briefing={briefing} onOpenItem={onOpenItem} onCompleteTask={onCompleteTask} />
          ) : (
          <SectionCard className="p-7 lg:p-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="m-0 text-xl font-semibold text-app-ink">Daily flow</h2>
                <p className="mt-1 text-sm leading-6 text-app-muted">
                  {formatDayLabel(briefing?.date || filters.date)} | {toneLabels[briefing?.dayTone] || 'Normal'} day
                </p>
              </div>
              <StatusPill tone="neutral">Generated {formatGeneratedAt(briefing?.generatedAt)}</StatusPill>
            </div>

            <div className="mt-7 space-y-6">
              {flowItems.length ? (
                flowItems.map(({ item, section, active }, index) => (
                  <TimelineRow
                    key={`${section}-${item.itemKey || item.sourceId || index}`}
                    item={item}
                    section={section}
                    active={active}
                    timezone={briefing?.timezone}
                    onOpenItem={onOpenItem}
                    onCompleteTask={onCompleteTask}
                  />
                ))
              ) : (
                <EmptyState
                  title="The day is quiet."
                  copy="No current events, upcoming events, must-do items, or small wins are showing for this briefing."
                  className="py-12 lg:py-12"
                />
              )}
            </div>
          </SectionCard>
          )}

          {!isRangeBriefing && headsUpItems.length ? (
            <CollapsedBriefingSection title="Heads up" count={headsUpItems.length}>
              <CompactGrid
                items={headsUpItems}
                emptyTitle="No heads up."
                emptyCopy="Nothing timely needs extra attention."
                renderItem={(item) => (
                  <RailItem
                    key={item.nudgeKey || item.title}
                    item={item}
                    icon={AlertTriangle}
                    tone="warning"
                    onOpenItem={onOpenItem}
                    onCompleteTask={onCompleteTask}
                  />
                )}
              />
            </CollapsedBriefingSection>
          ) : null}

          {!isRangeBriefing ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <CollapsedBriefingSection title="Earlier today" count={briefing?.passedToday?.length || 0}>
              <CompactGrid
                items={briefing?.passedToday || []}
                emptyTitle="Nothing earlier today."
                emptyCopy="No earlier calendar items are showing for this date."
                renderItem={(item) => (
                  <TimelineRow
                    key={item.itemKey || item.sourceId}
                    item={item}
                    section="Earlier"
                    muted
                    timezone={briefing?.timezone}
                    onOpenItem={onOpenItem}
                    onCompleteTask={onCompleteTask}
                  />
                )}
              />
            </CollapsedBriefingSection>

            <CollapsedBriefingSection title="Wrapped up today" count={winsCount}>
              <CompactGrid
                items={briefing?.alreadyDoneToday || []}
                emptyTitle="Nothing wrapped up yet."
                emptyCopy="Completed tasks, reminders, and events will show here."
                renderItem={(item) => (
                  <RailItem
                    key={item.itemKey || item.sourceId}
                    item={item}
                    icon={CheckCheck}
                    tone="success"
                    onOpenItem={onOpenItem}
                    onCompleteTask={onCompleteTask}
                  />
                )}
              />
            </CollapsedBriefingSection>
          </div>
          ) : null}
        </div>

        <aside className="space-y-7 2xl:sticky 2xl:top-24 2xl:self-start">
          <SectionCard>
            <SectionTitle
              title="Family sync"
              copy="The small set worth keeping visible."
              action={<StatusPill tone={syncItems.length ? 'accent' : 'neutral'}>{syncItems.length}</StatusPill>}
            />
            <div className="mt-5 space-y-3">
              {syncItems.length ? (
                syncItems.map((item, index) => (
                  <RailItem
                    key={item.itemKey || item.sourceId || index}
                    item={item}
                    icon={index === 0 ? Target : AlertTriangle}
                    tone={index === 0 ? 'success' : 'neutral'}
                    onOpenItem={onOpenItem}
                    onCompleteTask={onCompleteTask}
                  />
                ))
              ) : (
                <EmptyState title="No sync items." copy="Nothing needs the right rail for this briefing." className="py-8 lg:py-8" />
              )}
            </div>
          </SectionCard>

          <MiniMonth selectedDate={briefing?.date || filters.date} />

          <SectionCard>
            <SectionTitle
              title="Tomorrow prep"
              copy="A short look-ahead for the family."
              action={<StatusPill tone="neutral">{prepCount}</StatusPill>}
            />
            <div className="mt-5 space-y-3">
              {briefing?.tomorrowPrep?.length ? (
                briefing.tomorrowPrep.map((item) => (
                  <RailItem
                    key={item.nudgeKey || item.title}
                    item={item}
                    icon={Sparkles}
                    onOpenItem={onOpenItem}
                    onCompleteTask={onCompleteTask}
                  />
                ))
              ) : (
                <div className="rounded-[22px] border border-dashed border-app-border bg-app-surface-alt/55 px-5 py-10 text-center">
                  <Sparkles className="mx-auto h-8 w-8 text-app-muted/60" aria-hidden="true" />
                  <p className="mt-3 text-sm leading-6 text-app-muted">No prep nudges for tomorrow.</p>
                </div>
              )}
            </div>
          </SectionCard>

          <div className="overflow-hidden rounded-[22px] border border-app-border/70 bg-[linear-gradient(135deg,#0f766e,#1d4ed8)] p-6 text-white shadow-panel">
            <p className="text-xl font-semibold leading-7">Focus on what matters today.</p>
            <p className="mt-2 text-sm leading-6 text-white/75">
              {briefing?.headline || 'Use the daily flow first, then the sync rail.'}
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}

export default DailyBriefingView;
